import React, { useMemo, useState } from 'react';
import Select from '../../components/Select/Select';
import Button from '../../components/Button/Button';
import {
  groupComplectationRows,
  complectationOptions,
  defaultAnswer,
  buildComplectationResolution,
  NEW_COMPLECTATION,
} from './complectationGroups';
import styles from './complectationQuestions.module.css';

const carsWord = (n) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'машина';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'машины';
  return 'машин';
};

// Первый шаг ручной правки: названия комплектаций из выгрузки, которых нет в
// справочнике. По одной карточке на «модель + название»; ответ запоминается
// на бэке (алиас) и больше не спрашивается
const ComplectationQuestions = ({ rows, onCommit }) => {
  const groups = useMemo(() => groupComplectationRows(rows), [rows]);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(() => new Set());

  const answerOf = (group) => (group.key in answers ? answers[group.key] : defaultAnswer(group));

  const apply = async (toApply) => {
    const resolutions = toApply.flatMap((group) =>
      group.rows.map((row) => buildComplectationResolution(row, answerOf(group)))
    );
    if (resolutions.length === 0) return;
    const keys = toApply.map((g) => g.key);
    setBusy((prev) => new Set([...prev, ...keys]));
    try {
      await onCommit(resolutions);
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.delete(k));
        return next;
      });
    }
  };

  const ready = groups.filter((g) => answerOf(g) && !busy.has(g.key));

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <div className={styles.hint}>
          Этих комплектаций нет в справочнике модели. Выберите, какой из существующих они соответствуют, — или заведите
          новую.
        </div>
        <Button
          fill
          text={`Применить выбранные (${ready.length})`}
          onClick={() => apply(ready)}
          disabled={ready.length === 0}
        />
      </div>

      {groups.map((group) => {
        const answer = answerOf(group);
        const isBusy = busy.has(group.key);
        return (
          <div key={group.key} className={styles.card}>
            <div className={styles.cardInfo}>
              <div className={styles.cardTitle}>{group.title || 'без названия'}</div>
              <div className={styles.cardMeta}>
                {group.markName} {group.modelName} · {group.rows.length} {carsWord(group.rows.length)}
              </div>
            </div>
            <div className={styles.cardSelect}>
              <Select
                placeholder="Выберите комплектацию"
                options={complectationOptions(group)}
                value={answer}
                disabled={isBusy}
                onChange={(val) => setAnswers((prev) => ({ ...prev, [group.key]: val }))}
              />
              {answer?.value === NEW_COMPLECTATION && (
                <div className={styles.newHint}>
                  Новая комплектация создастся пустой — оснащение придётся заполнить в справочнике
                </div>
              )}
            </div>
            <div className={styles.cardButton}>
              <Button
                fill
                text={isBusy ? 'Применяется…' : 'Применить'}
                onClick={() => apply([group])}
                disabled={!answer || isBusy}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ComplectationQuestions;
